import clsx from "clsx"
import { FC, useCallback, useState } from "react"
import { BucketIcon, GameDeskShadowFrameImg } from "@/assets"
import { useAudioController } from "@/hooks/useAudioController"
import { useModals } from "@/modals"
import { useGame } from "@/providers/GameProvider"
import { ICard, useGetDeckLastCardMutation } from "@/store/apis/deckAPI"
import { useAppDispatch, useAppSelector } from "@/store/index"
import { AppSelectors } from "@/store/slices/app"
import { GameplaySelectors } from "@/store/slices/gameplay"
import { MatchSelectors } from "@/store/slices/match"
import { ICardFront } from "@/ui/Card"
import { OutlinedText } from "@/ui/OutlinedText"
import { ICardItem } from "."
import styles from "./GameDesk.module.scss"
import {
  useCardEvents,
  useCardGroups,
  useCardInit,
  useCardItems,
  useMatchEndReveal
} from "./hooks"
import {
  ArchiveCardList,
  ButtonTurn,
  CardList,
  DeckCardList,
  DeskFrame,
  GoldCardList,
  OpponentCardList,
  PlayerCardList,
  ViewCard
} from "./ui"

export const GameDesk: FC = () => {
  const dispatch = useAppDispatch()
  const {
    player,
    opponent,
    playerCards: playerCardsData,
    opponentCards: opponentCardsData,
    isPlayerCardsLoading,
    gameState,
    gamePrevState,
    isGameStateLoading,
    isMyTurn,
    handleDiscardCard,
    handlePlayCard,
    isMe,
    events,
    isEventsFulfilled,
    matchSession,
    sessionId,
    isMatchActive,
    refIsMatchActive,
    refIsMyTurn
  } = useGame()
  const { openModal } = useModals()
  const { playSound } = useAudioController()

  const isDevMode = useAppSelector(AppSelectors.selectIsDevMode())
  const isDesktop = useAppSelector(AppSelectors.selectIsDesktop())
  const isMatchSocketConnected = useAppSelector(
    MatchSelectors.selectIsSocketConnected()
  )
  const isGameplaySocketConnected = useAppSelector(
    GameplaySelectors.selectSocketConnection()
  )

  const {
    items,
    setItems,
    refItems,
    isCardAnimation,
    setIsCardAnimation,
    refUserSelectCardInAnimation,
    refFinishAnimationAction,
    initGuards,
    checkFullInit,
    withFlip,
    addTo,
    moveTo,
    restoreCardToHand
  } = useCardItems()

  useCardInit({
    playerCardsData,
    opponentCardsData,
    isPlayerCardsLoading,
    gameState,
    isGameStateLoading,
    isMatchActive,
    player,
    opponent,
    addTo,
    setItems,
    initGuards,
    checkFullInit
  })

  const [viewCardData, setViewCardData] = useState<ICard | undefined>()

  const [getDeckLastCard] = useGetDeckLastCardMutation()

  const {
    user: playerCards,
    opponent: opponentCards,
    deck: deckCards,
    active: activeCards,
    archive: archiveCards,
    "removed-opponent": removedOpponentCards,
    "removed-player": removedPlayerCards,
    preview: previewCards,
    "opponent-view": opponentViewCards,
    gold: goldCards
  } = useCardGroups(items)

  useCardEvents({
    events,
    isEventsFulfilled,
    gameState,
    gamePrevState,
    player,
    opponent,
    sessionId,
    isMe,
    isCardAnimation,
    refItems,
    refUserSelectCardInAnimation,
    setItems,
    withFlip,
    moveTo,
    addTo,
    opponentCards,
    goldCards,
    dispatch,
    playSound,
    getDeckLastCard
  })

  useMatchEndReveal({
    isMatchActive,
    matchSession,
    gameState,
    isMe,
    refItems,
    setItems,
    moveTo
  })

  const handleDiscard = useCallback(
    (el: ICardItem) => {
      if (refFinishAnimationAction.current) return

      const cardData = el.data

      playSound("card-discarded")
      setIsCardAnimation(true)

      moveTo(el.id, "removed-player", "player-removed")

      refFinishAnimationAction.current = async () => {
        const success = await handleDiscardCard(el?.data?._id)
        if (!success) {
          restoreCardToHand(el.id, cardData)
        }
      }
    },
    [
      moveTo,
      restoreCardToHand,
      handleDiscardCard,
      playSound,
      setIsCardAnimation
    ]
  )

  const handleActivateCard = useCallback(
    (el: ICardItem) => {
      if (refFinishAnimationAction.current) return

      const cardItemId = el.id
      const cardData = el.data

      setIsCardAnimation(true)
      moveTo(el.id, "active", "user-active")

      const actionPromise = handlePlayCard(el?.data?._id)

      refFinishAnimationAction.current = async () => {
        const success = await actionPromise
        if (!success) {
          restoreCardToHand(cardItemId, cardData)
        }
      }
    },
    [moveTo, restoreCardToHand, handlePlayCard, setIsCardAnimation]
  )

  const handleViewCard = useCallback((data?: ICardFront) => {
    setViewCardData(data)
  }, [])

  const handleOpenGoldCards = () => {
    openModal("matchGoldCards")
  }

  return (
    <div className={clsx(styles.GameDesk)}>
      {isMatchActive && (
        <div className={clsx(styles.GameDesk_socket)}>
          <div
            className={clsx({
              [styles.active]: isMatchSocketConnected
            })}
          />
          <div
            className={clsx({
              [styles.active]: isGameplaySocketConnected
            })}
          />
        </div>
      )}
      {!isDesktop && (
        <div className={clsx(styles.GameDesk_sessionNumber)}>
          {matchSession?.sessionId}
        </div>
      )}

      {viewCardData && (
        <ViewCard
          data={viewCardData}
          isMatchActive={isMatchActive}
          onClose={() => setViewCardData(undefined)}
        />
      )}
      {isMatchActive && (
        <div
          id="game-desk-discard-field"
          className={clsx(styles.GameDesk_discardField)}
        >
          <div
            id="game-desk-discard-field-gradient"
            className={clsx(styles.GameDesk_discardField_gradient)}
          >
            <div className={clsx(styles.GameDesk_discardField_discardCard)}>
              <BucketIcon />
            </div>
          </div>
          <div
            id="game-desk-discard-field-overlay"
            className={clsx(styles.GameDesk_discardField_overlay)}
          />
        </div>
      )}
      <div
        className={clsx(styles.GameDesk_previewCardField, {
          [styles.active]: previewCards.length > 0
        })}
        onClick={() => {
          moveTo(previewCards?.[0]?.id, "user")
        }}
      >
        <div className={clsx(styles.GameDesk_previewCardField_overlay)} />
        <div className={clsx(styles.GameDesk_previewCardField_cardPlace)}>
          <CardList items={previewCards} isMatchActive={isMatchActive} />
        </div>
      </div>
      <div className={clsx(styles.GameDesk_content)}>
        <DeskFrame />
        <div className={clsx(styles.GameDesk_frame, styles.shadow)}>
          <img src={GameDeskShadowFrameImg} alt="desk shadow frame" />
        </div>

        {isMatchActive && (
          <div
            className={clsx(styles.GameDesk_goldCard)}
            onClick={handleOpenGoldCards}
          >
            <GoldCardList items={goldCards} isMatchActive={isMatchActive} />
          </div>
        )}

        <div className={clsx(styles.GameDesk_opponentCards)}>
          <OpponentCardList
            items={opponentCards}
            isMatchActive={isMatchActive}
          />
        </div>
        <div className={clsx(styles.GameDesk_opponentViewCards)}>
          <CardList
            items={opponentViewCards}
            isMatchActive={isMatchActive}
            onClickCard={setViewCardData}
          />
        </div>
        <div className={clsx(styles.GameDesk_removedOpponentCards)}>
          <CardList
            items={removedOpponentCards}
            isMatchActive={isMatchActive}
          />
        </div>
        {isMatchActive && (
          <div className={clsx(styles.GameDesk_deckCards)}>
            <DeckCardList items={deckCards} isMatchActive={isMatchActive} />
            {isDevMode && (
              <div className={clsx(styles.GameDesk_deckCards_overlay)}>
                <OutlinedText
                  className={styles.GameDesk_deckCards_overlay_text}
                >
                  {gameState?.deckSize}
                </OutlinedText>
              </div>
            )}
          </div>
        )}
        <div
          className={clsx(styles.GameDesk_playerCards, {
            [styles.notMyTurn]: !isMyTurn
          })}
        >
          <PlayerCardList
            items={playerCards}
            isMatchActive={isMatchActive}
            player={player}
            refIsMyTurn={refIsMyTurn}
            refIsMatchActive={refIsMatchActive}
            onDiscard={handleDiscard}
            onActivateCard={handleActivateCard}
            onViewCard={handleViewCard}
          />
        </div>
        <div className={clsx(styles.GameDesk_activeCards)}>
          <CardList
            items={activeCards}
            isMatchActive={isMatchActive}
            onClickCard={setViewCardData}
          />
        </div>
        <div className={clsx(styles.GameDesk_removedPlayerCards)}>
          <CardList items={removedPlayerCards} isMatchActive={isMatchActive} />
        </div>

        <div className={clsx(styles.GameDesk_archiveCards)}>
          <ArchiveCardList
            items={archiveCards}
            isMatchActive={isMatchActive}
            onClickCard={setViewCardData}
          />
        </div>
        {isMatchActive && (
          <ButtonTurn className={clsx(styles.GameDesk_buttonTurn)} />
        )}
        <div
          id="game-desk-active-card-field"
          className={clsx(styles.GameDesk_activeCardField)}
        />
      </div>
    </div>
  )
}
